import { RefObject, useEffect, useRef } from 'react';
import { restoreFocusWithoutMobileComposer } from '../utils/composerFocus';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

interface UseModalAccessibilityOptions {
  enabled?: boolean;
  onClose?: () => void;
  initialFocusRef?: RefObject<HTMLElement>;
  restoreFocus?: boolean;
}

const isVisible = (element: HTMLElement) =>
  element.getClientRects().length > 0 && window.getComputedStyle(element).visibility !== 'hidden';

const getFocusableElements = (container: HTMLElement) =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter((element) => isVisible(element) && element.getAttribute('aria-hidden') !== 'true');

export const useModalAccessibility = <T extends HTMLElement>(
  modalRef: RefObject<T>,
  {
    enabled = true,
    onClose,
    initialFocusRef,
    restoreFocus = true
  }: UseModalAccessibilityOptions = {}
) => {
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!enabled) return;

    const modal = modalRef.current;
    if (!modal) return;

    previousFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    const focusTimer = window.setTimeout(() => {
      const requestedFocus = initialFocusRef?.current;
      const focusTarget = requestedFocus && modal.contains(requestedFocus) && isVisible(requestedFocus)
        ? requestedFocus
        : getFocusableElements(modal)[0] || modal;
      focusTarget.focus();
    }, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onCloseRef.current) {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements(modal);
      if (focusableElements.length === 0) {
        event.preventDefault();
        modal.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

      if (event.shiftKey) {
        if (activeElement === firstElement || !activeElement || !modal.contains(activeElement)) {
          event.preventDefault();
          lastElement.focus();
        }
        return;
      }

      if (activeElement === lastElement || !activeElement || !modal.contains(activeElement)) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown, true);

      const previousFocus = previousFocusRef.current;
      if (restoreFocus && previousFocus?.isConnected) {
        restoreFocusWithoutMobileComposer(previousFocus);
      }
    };
  }, [enabled, initialFocusRef, modalRef, restoreFocus]);
};
